/**
 * Pixel size from a file's header, without decoding (fix round 1, SP-5/BU-8): a server-edited file
 * the browser cannot open (EPS/RAW source, TIFF/HEIC/AVIF output) still gets the edit-stack
 * `name-WxH.ext` filename from the bytes the edge returned. PNG, JPEG, GIF, BMP, WebP, TIFF, ICO and
 * HEIF/AVIF (`ispe`). null when the header is not one of those or is truncated.
 */
import type { Size } from '../model/edit-stack';

export function probeSize(b: Uint8Array): Size | null {
    try {
        const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
        const ok = (w: number, h: number): Size | null => (w > 0 && h > 0 ? { width: w, height: h } : null);
        const at = (i: number, s: string) => s.split('').every((c, j) => b[i + j] === c.charCodeAt(0));
        if (b.length >= 24 && b[0] === 0x89 && at(1, 'PNG') && at(12, 'IHDR')) return ok(dv.getUint32(16), dv.getUint32(20));
        if (b.length >= 10 && at(0, 'GIF8')) return ok(dv.getUint16(6, true), dv.getUint16(8, true));
        if (b.length >= 26 && at(0, 'BM')) return ok(Math.abs(dv.getInt32(18, true)), Math.abs(dv.getInt32(22, true)));
        if (b.length >= 30 && at(0, 'RIFF') && at(8, 'WEBP')) {
            const u24 = (i: number) => b[i] | (b[i + 1] << 8) | (b[i + 2] << 16);
            if (at(12, 'VP8X')) return ok(u24(24) + 1, u24(27) + 1);
            if (at(12, 'VP8L')) { const v = dv.getUint32(21, true); return ok((v & 0x3fff) + 1, ((v >> 14) & 0x3fff) + 1); }
            if (at(12, 'VP8 ')) return ok(dv.getUint16(26, true) & 0x3fff, dv.getUint16(28, true) & 0x3fff);
            return null;
        }
        if (b.length >= 4 && b[0] === 0xff && b[1] === 0xd8) {
            let i = 2;
            while (i + 9 < b.length) {
                if (b[i] !== 0xff) { i++; continue; }
                const m = b[i + 1];
                if (m === 0xff) { i++; continue; }
                if (m === 0xd8 || m === 0x01 || (m >= 0xd0 && m <= 0xd7)) { i += 2; continue; }
                if (m >= 0xc0 && m <= 0xcf && m !== 0xc4 && m !== 0xc8 && m !== 0xcc) return ok(dv.getUint16(i + 7), dv.getUint16(i + 5));
                i += 2 + dv.getUint16(i + 2);
            }
            return null;
        }
        if (b.length >= 8 && ((b[0] === 0x49 && b[1] === 0x49) || (b[0] === 0x4d && b[1] === 0x4d))) {
            const le = b[0] === 0x49; if (dv.getUint16(2, le) !== 42) return null;
            const ifd = dv.getUint32(4, le); const n = dv.getUint16(ifd, le); let w = 0, h = 0;
            for (let k = 0; k < n; k++) {
                const e = ifd + 2 + k * 12; const tag = dv.getUint16(e, le), type = dv.getUint16(e + 2, le);
                const v = type === 3 ? dv.getUint16(e + 8, le) : dv.getUint32(e + 8, le);
                if (tag === 256) w = v; else if (tag === 257) h = v;
            }
            return ok(w, h);
        }
        if (b.length >= 12 && at(4, 'ftyp')) {
            // the largest `ispe` is the primary image (grid tiles carry smaller ones)
            let best: Size | null = null;
            for (let i = 4; i + 16 <= b.length; i++) {
                if (b[i] === 0x69 && at(i, 'ispe')) {
                    const s = ok(dv.getUint32(i + 8), dv.getUint32(i + 12));
                    if (s && (!best || s.width * s.height > best.width * best.height)) best = s;
                }
            }
            // IMAGEEDITOR-16: a stray byte match can read a tiny bogus size (the
            // Original line showed "1 × 1 · HEIC"). No real photo is under 8 px:
            // report "unknown" and let the decoder supply the real size.
            return best && best.width >= 8 && best.height >= 8 ? best : null;
        }
        if (b.length >= 8 && dv.getUint16(0, true) === 0 && dv.getUint16(2, true) === 1 && dv.getUint16(4, true) > 0) return ok(b[6] || 256, b[7] || 256);
        return null;
    } catch { return null; }
}

/** Print resolution in dots per inch. */
export interface Dpi { x: number; y: number }

/**
 * Source DPI from a file's head (geometry-7): JPEG JFIF APP0 or EXIF IFD0
 * resolution, PNG `pHYs`, BMP pixels-per-metre. null when the file states no
 * physical resolution (JFIF units 0, no pHYs, BMP 0) — "unknown", not 72.
 * TIFF resolution comes from the UTIF page instead (tiff.ts `tiffPageDpi`),
 * because a TIFF's IFD can sit at the end of the file.
 */
export function probeDpi(b: Uint8Array): Dpi | null {
    try {
        const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
        const at = (i: number, s: string) => s.split('').every((c, j) => b[i + j] === c.charCodeAt(0));
        const dpi = (x: number, y: number, perCm: boolean): Dpi | null => {
            const k = perCm ? 2.54 : 1;
            return x > 0 && y > 0 ? { x: Math.round(x * k), y: Math.round(y * k) } : null;
        };
        if (b.length >= 33 && b[0] === 0x89 && at(1, 'PNG')) {
            let i = 8;
            while (i + 12 <= b.length) {
                const len = dv.getUint32(i);
                if (at(i + 4, 'IDAT') || at(i + 4, 'IEND')) return null;
                if (at(i + 4, 'pHYs') && i + 17 <= b.length) {
                    if (b[i + 16] !== 1) return null;
                    const x = dv.getUint32(i + 8), y = dv.getUint32(i + 12);
                    return dpi(x * 0.0254, y * 0.0254, false);
                }
                i += 12 + len;
            }
            return null;
        }
        if (b.length >= 46 && at(0, 'BM')) {
            const x = dv.getInt32(38, true), y = dv.getInt32(42, true);
            return dpi(x * 0.0254, y * 0.0254, false);
        }
        if (b.length >= 4 && b[0] === 0xff && b[1] === 0xd8) {
            let i = 2;
            let jfif: Dpi | null = null;
            while (i + 4 <= b.length && b[i] === 0xff) {
                const m = b[i + 1];
                if (m === 0xda || m === 0xd9) break;
                const len = dv.getUint16(i + 2);
                if (m === 0xe0 && at(i + 4, 'JFIF') && i + 18 <= b.length) {
                    const units = b[i + 11];
                    if (units === 1 || units === 2) jfif = dpi(dv.getUint16(i + 12), dv.getUint16(i + 14), units === 2);
                } else if (m === 0xe1 && at(i + 4, 'Exif') && i + 2 + len <= b.length) {
                    const t = i + 10;
                    const le = b[t] === 0x49;
                    const ifd = t + dv.getUint32(t + 4, le);
                    const n = dv.getUint16(ifd, le);
                    let x = 0, y = 0, unit = 2;
                    for (let k = 0; k < n; k++) {
                        const e = ifd + 2 + k * 12;
                        const tag = dv.getUint16(e, le);
                        if (tag === 0x011a || tag === 0x011b) {
                            const off = t + dv.getUint32(e + 8, le);
                            const den = dv.getUint32(off + 4, le);
                            const v = den ? dv.getUint32(off, le) / den : 0;
                            if (tag === 0x011a) x = v; else y = v;
                        } else if (tag === 0x0128) {
                            unit = dv.getUint16(e + 8, le);
                        }
                    }
                    if (!jfif && unit !== 1 && x > 0) jfif = dpi(x, y || x, unit === 3);
                }
                i += 2 + len;
            }
            return jfif;
        }
        return null;
    } catch { return null; }
}
